import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import * as styles from "./Modal.module.css";

const focusableElements =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export enum ModalInitialFocus {
  /** Focus the first button (default) */
  FirstButton = "firstButton",
  /** Focus the first input, select, or textarea */
  FirstInput = "firstInput",
  /** Focus the first focusable element regardless of type */
  FirstFocusable = "firstFocusable",
  /** Focus the modal container itself (no specific element) */
  Container = "container",
}

const initialFocusSelectors: Record<ModalInitialFocus, string | null> = {
  [ModalInitialFocus.FirstButton]: "button",
  [ModalInitialFocus.FirstInput]:
    "input:not([disabled]), select:not([disabled]), textarea:not([disabled])",
  [ModalInitialFocus.FirstFocusable]: focusableElements,
  [ModalInitialFocus.Container]: null,
};

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  className?: string;
  ariaLabelledBy?: string;
  initialFocus?: ModalInitialFocus;
  closeOnOutsideClick?: boolean;
};

export const Modal = ({
  isOpen,
  onClose,
  children,
  width,
  className,
  ariaLabelledBy,
  initialFocus = ModalInitialFocus.FirstButton,
  closeOnOutsideClick = true,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Prevent background scrolling
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    previousActiveElement.current = document.activeElement as HTMLElement;
    const selector = initialFocusSelectors[initialFocus];
    const target = selector
      ? modalRef.current?.querySelector<HTMLElement>(selector)
      : modalRef.current;
    target?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }

      if (e.key === "Tab") {
        const focusableContent = modalRef.current?.querySelectorAll(focusableElements);
        if (!focusableContent || focusableContent.length === 0) {
          return;
        }

        const firstFocusable = focusableContent[0] as HTMLElement;
        const lastFocusable = focusableContent[focusableContent.length - 1] as HTMLElement;

        if (e.shiftKey && document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        } else if (!e.shiftKey && document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      // Restore original overflow when modal closes
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousActiveElement.current?.focus();
    };
  }, [initialFocus, isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleOutsideClick = (e: React.MouseEvent) => {
    if (closeOnOutsideClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modal = (
    <div
      className={styles.modalOverlay}
      data-testid="modal-overlay"
      role="presentation"
      onMouseDown={handleOutsideClick}
    >
      <div className={styles.modalWrapper} role="presentation">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          className={className ? `${styles.modalContent} ${className}` : styles.modalContent}
          style={width ? { width } : undefined}
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    </div>
  );

  return typeof document === "undefined" ? modal : createPortal(modal, document.body);
};
